import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { AdminUser, PluginAccess } from '../types';

export class AdminApiClient {
  private email: string | undefined;

  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  /** Set the caller's email so all admin-protected requests include it */
  setEmail(email: string) {
    this.email = email;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('admin');
  }

  private emailParam(): string {
    return this.email ? `?email=${encodeURIComponent(this.email)}` : '';
  }

  async getAdmins(): Promise<AdminUser[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/admins${this.emailParam()}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch admins: ${response.status}`);
    }
    return response.json();
  }

  async addAdmin(email: string): Promise<AdminUser> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/admins${this.emailParam()}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Failed to add admin: ${response.status} ${text}`);
    }
    return response.json();
  }

  async removeAdmin(id: number): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/admins/${id}${this.emailParam()}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`Failed to remove admin: ${response.status}`);
    }
  }

  async getPluginAccess(): Promise<PluginAccess[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/plugins${this.emailParam()}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch plugin access: ${response.status}`);
    }
    return response.json();
  }

  async updatePluginAccess(
    pluginName: string,
    allowedUsers: string,
  ): Promise<PluginAccess> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/plugins/${pluginName}${this.emailParam()}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ allowed_users: allowedUsers }),
      },
    );
    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        `Failed to update plugin access: ${response.status} ${text}`,
      );
    }
    return response.json();
  }

  async checkAdmin(email?: string): Promise<{ isAdmin: boolean }> {
    const baseUrl = await this.getBaseUrl();
    const params = email ? `?email=${encodeURIComponent(email)}` : '';
    const response = await this.fetchApi.fetch(`${baseUrl}/check-admin${params}`);
    if (!response.ok) {
      throw new Error(`Failed to check admin status: ${response.status}`);
    }
    return response.json();
  }

  async checkAccess(email?: string): Promise<Record<string, boolean>> {
    const baseUrl = await this.getBaseUrl();
    const params = email ? `?email=${encodeURIComponent(email)}` : '';
    const response = await this.fetchApi.fetch(`${baseUrl}/check-access${params}`);
    if (!response.ok) {
      throw new Error(`Failed to check access: ${response.status}`);
    }
    return response.json();
  }
}
